import { inject } from '@angular/core';
import { ApiService } from '@libs/core/base-config';
import {
  AsyncState,
  asResult,
  computedFailure,
  dataFromState,
  initialState,
  loadingState,
  successState,
} from '@libs/core/lib/state/async-states';
import { walletTransactionsList } from '@libs/mtjr/shared/generated/entities/wallet-transaction/service/wallet-transaction.service';
import { Dtos } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';

export function withWalletTransactionsList() {
  type WalletTransactionsListState = {
    walletTransactionsListState: AsyncState<Dtos.WalletTransactionsListResponse>;
    walletTransactionsListInput: Dtos.WalletTransactionsListQueryParams;
    walletTransactionsListCanFetchNext: boolean;
    walletTransactionsListFetchingNext: boolean;
  };
  return signalStoreFeature(
    withState<WalletTransactionsListState>({
      walletTransactionsListState: initialState(),
      walletTransactionsListInput: { page: 1, totalPerPage: 20 },
      walletTransactionsListCanFetchNext: false,
      walletTransactionsListFetchingNext: false,
    }),

    withMethods((store, apiService = inject(ApiService)) => ({
      async walletTransactionsListPage(input?: Partial<Dtos.WalletTransactionsListQueryParams>) {
        const mergedInput = { ...store.walletTransactionsListInput(), ...input };
        patchState(store, {
          walletTransactionsListState: mergedInput.page === 1 ? loadingState() : store.walletTransactionsListState(),
          walletTransactionsListInput: mergedInput,
          walletTransactionsListFetchingNext: mergedInput.page > 1,
        });
        const result = await asResult(walletTransactionsList(apiService, store.walletTransactionsListInput()));
        if (result.success) {
          const cState = store.walletTransactionsListState();
          let walletTransactions = result.data.walletTransactions;
          if (cState.success && mergedInput.page > 1) {
            walletTransactions = [...cState.data.walletTransactions, ...walletTransactions];
          }
          patchState(store, {
            walletTransactionsListState: successState({ ...result.data, walletTransactions: walletTransactions }),
            walletTransactionsListCanFetchNext: result.data.walletTransactions.length === mergedInput.totalPerPage,
            walletTransactionsListFetchingNext: false,
          });
        } else {
          patchState(store, {
            walletTransactionsListState: result,
            walletTransactionsListCanFetchNext: false,
            walletTransactionsListFetchingNext: false,
          });
        }
      },

      async walletTransactionsListFetchNextPage() {
        if (store.walletTransactionsListCanFetchNext() === false) return;
        const input = store.walletTransactionsListInput();
        await this.walletTransactionsListPage({ ...input, page: input.page + 1 });
      },
    })),

    withComputed((store) => ({
      walletTransactions: dataFromState(store.walletTransactionsListState, (data) => data?.walletTransactions ?? []),
      walletTransactionsFailure: computedFailure(store.walletTransactionsListState),
    })),
  );
}
